import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';

import { filesAPIService } from '~/api/files';
import APP_DEFAULT_IMAGE from '~/assets/icons/APP_DEFAULT_IMAGE.png';
import ContentQRBuilderHeader from '~/components/builder/ContentQRBuilderHeader';
import NameAndOrganizationBlockOldFlow from '~/components/builder/NameAndOrganizationBlockOldFlow';
import PasswordProtectionBlockOldFlow from '~/components/builder/PasswordProtectionBlockOldFlow';
import QrTypeTopCard from '~/components/builder/QrTypeTopCard';
import { SettingsBlock } from '~/components/organisms/editor/SettingsBlock';
import { modelMicrositeService } from '~/components/organisms/previewBar/model-data';
import {
  QR_CODE_BUILDER_CREATED_ID_PARAM_NAME,
  QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME,
  QRCODE_DYNAMIC_MICROSITE_TYPE,
} from '~/constants/qr-code';
import { useAppDispatch } from '~/hooks/useRedux';
import { appsSlice } from '~/store/reducers/previewScreens/apps';
import { getAppsReducer } from '~/store/reducers/previewScreens/apps/selectors';
import { getQrCodeReducerData } from '~/store/reducers/qr-code/selectors';
import { getFileFromLocalSource } from '~/utils/common';

import { useQRUrlValidation } from '../../hooks/useQRUrlValidation';

import { Appearance } from './components/Appearance';
import { BasicInformation } from './components/BasicInformation';

const { actions } = appsSlice;

export default function AppsTemplate() {
  const dispatch = useAppDispatch();

  const {
    logo,
    micrositeData,
    stats: { url, scan, isAutogeneratedUrl, isLimitsEnabled },
  } = useSelector(getAppsReducer);
  const appReducer = useSelector(getAppsReducer);
  const qrCodeReducer = useSelector(getQrCodeReducerData);

  const [searchParams] = useSearchParams();
  const createdId = searchParams.get(QR_CODE_BUILDER_CREATED_ID_PARAM_NAME);
  const isEditPage = !!searchParams.get(QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME);

  const urlValidationParams = useQRUrlValidation(url);

  const renderPreviewImage = async () => {
    const localFile = await getFileFromLocalSource(`${window.location.origin}${APP_DEFAULT_IMAGE}`);
    const storageResponse = await filesAPIService.uploadImage(localFile);

    dispatch(actions.setLogo({ path: APP_DEFAULT_IMAGE, imageStorageId: storageResponse.data }));
  };

  useEffect(() => {
    if (!micrositeData && !createdId) {
      !logo ? renderPreviewImage() : modelMicrositeService.app(appReducer, qrCodeReducer, { dispatch, actions });
    }
  }, [logo]);

  useEffect(() => {
    if (createdId && !micrositeData?.id) {
      modelMicrositeService.app(appReducer, qrCodeReducer, { dispatch, actions });
    }
  }, []);

  const handleChangeStatsField = (val) => {
    dispatch(actions.setStats(val));
  };

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <ContentQRBuilderHeader />
      <QrTypeTopCard type={QRCODE_DYNAMIC_MICROSITE_TYPE.APP} />
      <Appearance />
      <BasicInformation />
      <NameAndOrganizationBlockOldFlow onChangeName={(val) => dispatch(actions.setTitle(val))} />
      <PasswordProtectionBlockOldFlow />
      <SettingsBlock
        onChange={handleChangeStatsField}
        urlValidationParams={urlValidationParams}
        scanCount={scan}
        isLimitsEnabled={isLimitsEnabled}
        isAutogeneratedUrl={isAutogeneratedUrl}
        defaultUrlValue={url}
        urlFromBE={qrCodeReducer?.data?.uri || ''}
        isEditPage={isEditPage}
      />
    </Box>
  );
}
